import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Chart as ChartJS } from "chart.js/auto";
import { Bar } from "react-chartjs-2";
import { Select, Spin, Tabs } from "antd";
import PurchaseOrderServices from "../../services/purchaseOrder.service";
import { BlobToDownloadFile } from "../../helper/commanFunction";
import "../../assets/css/po2-design.css";

const usd = (v) => (v != null ? `$${Number(v).toFixed(2)}` : "-");

// Task 1920-1927 (Feature 1866 / F8): organizer dashboard. Backend is fully built
// (Organizer role, Hangfire aggregation, headline/leaderboards/timing/relationships) -
// this is the frontend, one screen per show with a picker, drilling through into F7's
// OrderReport.js for the underlying orders rather than building a second order list.
//
// Epic 1857 UI-redesign (Phase 6): restyled to the prototype's oDash() layout - primary
// and secondary .po2-kpis rows, .po2-lead leaderboard rows (rank badge/name/movement/
// amount) instead of a paginated data table (matches the prototype's own minimal
// leaderboard - full Orders/Units detail is still one click away via drill-through and
// in the CSV export). antd's Tabs is kept as-is (already visually neutral, not worth
// rebuilding); the chart keeps chart.js/react-chartjs-2, only recolored.
const OrganizerDashboard = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const navigate = useNavigate();

  const [shows, setShows] = useState([]);
  const [showsLoading, setShowsLoading] = useState(true);
  const [activeShowId, setActiveShowId] = useState(null);

  const [headline, setHeadline] = useState(null);
  const [leaderboards, setLeaderboards] = useState(null);
  const [timing, setTiming] = useState(null);
  const [relationships, setRelationships] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    PurchaseOrderServices.GetMyShows()
      .then((res) => {
        setShowsLoading(false);
        if (res?.data?.status) {
          const list = res.data.responseData || [];
          setShows(list);
          if (list.length > 0) setActiveShowId(list[0].showId);
        }
      })
      .catch(() => setShowsLoading(false));
  }, []);

  useEffect(() => {
    if (!activeShowId) return;
    setLoading(true);
    Promise.all([
      PurchaseOrderServices.GetDashboardHeadline(activeShowId),
      PurchaseOrderServices.GetDashboardLeaderboards(activeShowId),
      PurchaseOrderServices.GetDashboardTiming(activeShowId),
      PurchaseOrderServices.GetDashboardRelationships(activeShowId),
    ])
      .then(([h, l, t2, r]) => {
        setLoading(false);
        if (h?.data?.status) setHeadline(h.data.responseData);
        if (l?.data?.status) setLeaderboards(l.data.responseData);
        if (t2?.data?.status) setTiming(t2.data.responseData);
        if (r?.data?.status) setRelationships(r.data.responseData);
      })
      .catch(() => setLoading(false));
  }, [activeShowId]);

  const timingChartData = useMemo(() => {
    if (!timing?.dailySeries) return null;
    return {
      labels: timing.dailySeries.map((d) => new Date(d.summaryDate).toLocaleDateString()),
      datasets: [
        { label: t("Order Value"), data: timing.dailySeries.map((d) => d.orderValue), backgroundColor: "#2a41a3" },
        { label: t("Committed Value"), data: timing.dailySeries.map((d) => d.committedValue), backgroundColor: "#2bb673" },
      ],
    };
  }, [timing, t]);

  const drillInto = (filters) => navigate("/orderReport", { state: { showId: activeShowId, ...filters } });

  const movementBadge = (r) => {
    if (r.rankMovement == null) return <span className="po2-mv" style={{ color: "var(--po2-muted-2)" }}>{t("new")}</span>;
    if (r.rankMovement > 0) return <span className="po2-mv po2-trend up">▲{r.rankMovement}</span>;
    if (r.rankMovement < 0) return <span className="po2-mv po2-trend down">▼{Math.abs(r.rankMovement)}</span>;
    return <span className="po2-mv po2-trend flat">—</span>;
  };

  const LeaderboardList = ({ entries }) => (
    <div className="po2-tblscroll" style={{ maxHeight: 420, border: "none" }}>
      {(entries || []).map((r, i) => (
        <div className="po2-lead" key={r.partyDealerId}>
          <div className={`po2-rk ${i === 0 ? "po2-g" : ""}`}>{r.currentRank}</div>
          <div className="po2-nm">
            <a onClick={() => drillInto({ counterpartyDealerId: r.partyDealerId })}>{r.partyCompanyName || `#${r.partyDealerId}`}</a>
          </div>
          {movementBadge(r)}
          <span className="po2-amt2">{usd(r.orderValue)}</span>
        </div>
      ))}
      {(!entries || entries.length === 0) && (
        <div className="po2-empty">
          <p>{t("No data yet.")}</p>
        </div>
      )}
    </div>
  );

  const handleExportLeaderboardCsv = () => {
    if (!leaderboards) return;
    const lines = [["Role", "Rank", "Company", "Orders", "Units", "Value"].join(",")];
    [
      ["Vendor", leaderboards.vendors],
      ["Retailer", leaderboards.retailers],
    ].forEach(([role, entries]) => {
      (entries || []).forEach((r) => {
        lines.push(
          [role, r.currentRank, `"${(r.partyCompanyName || "").replace(/"/g, '""')}"`, r.orderCount, r.unitCount, r.orderValue].join(",")
        );
      });
    });
    BlobToDownloadFile(lines.join("\n"), `OrganizerLeaderboard-${activeShowId}-${Date.now()}.csv`);
  };

  if (showsLoading) {
    return (
      <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"}>
        <Spin spinning />
      </div>
    );
  }

  if (shows.length === 0) {
    return (
      <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"}>
        <div className="po2-phead">
          <h1>{t("Organizer Dashboard")}</h1>
        </div>
        <div className="po2-panel">
          <div className="po2-empty">
            <div className="po2-ic">◇</div>
            <p>{t("No shows are assigned to you as organizer yet.")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"}>
      <div className="po2-phead">
        <div>
          <h1>{t("Organizer Dashboard")}</h1>
        </div>
        <div className="po2-right">
          <Select
            style={{ width: 320 }}
            value={activeShowId}
            onChange={setActiveShowId}
            options={shows.map((s) => ({ value: s.showId, label: `${s.showName} (${s.city}, ${s.state})` }))}
          />
        </div>
      </div>

      <Spin spinning={loading}>
        {headline && (
          <>
            <div className="po2-kpis">
              <div className="po2-kpi">
                <div className="po2-lab">{t("Total Orders")}</div>
                <div className="po2-val">{headline.totalOrderCount}</div>
              </div>
              <div className="po2-kpi">
                <div className="po2-lab">{t("Total Order Value")}</div>
                <div className="po2-val">{usd(headline.totalOrderValue)}</div>
              </div>
              <div className="po2-kpi">
                <div className="po2-lab">{t("Committed Value")}</div>
                <div className="po2-val">{usd(headline.committedValue)}</div>
              </div>
              <div className="po2-kpi">
                <div className="po2-lab">{t("Uncommitted (Pipeline)")}</div>
                <div className="po2-val">{usd(headline.uncommittedValue)}</div>
              </div>
            </div>
            <div className="po2-kpis">
              <div className="po2-kpi po2-sec">
                <div className="po2-lab">{t("Exhibitor Coverage")}</div>
                <div className="po2-val">
                  {headline.exhibitorCoveragePct != null ? `${headline.exhibitorCoveragePct}%` : t("N/A")}
                </div>
                <div className="po2-sub">{headline.vendorCount}/{headline.registeredVendorCount} {t("exhibitors")}</div>
              </div>
            </div>
          </>
        )}

        <div className="po2-panel po2-flush">
          <Tabs>
            <Tabs.TabPane tab={t("Leaderboards")} key="leaderboards">
              <div style={{ padding: 16 }}>
                {leaderboards ? (
                  <>
                    <div className="po2-ph" style={{ marginTop: 0 }}>
                      <span className="po2-desc">
                        {leaderboards.hasPriorEdition
                          ? t("Rank movement vs prior edition")
                          : t("Rank movement needs a prior edition")}
                      </span>
                      <div className="po2-right">
                        <button className="po2-btn po2-neutral po2-sm" onClick={handleExportLeaderboardCsv}>
                          {t("Export CSV")}
                        </button>
                      </div>
                    </div>
                    {!leaderboards.hasPriorEdition && (
                      <div className="po2-insight po2-warn">
                        {t("No prior edition of this show exists yet - rank movement will appear once one does.")}
                      </div>
                    )}
                    <div className="po2-grid po2-g2">
                      <div className="po2-panel">
                        <div className="po2-ph"><h3>{t("Top vendors by dollars")}</h3></div>
                        <LeaderboardList entries={leaderboards.vendors} />
                      </div>
                      <div className="po2-panel">
                        <div className="po2-ph"><h3>{t("Top retailers by dollars")}</h3></div>
                        <LeaderboardList entries={leaderboards.retailers} />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="po2-empty"><p>{t("No data yet.")}</p></div>
                )}
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab={t("Timing & Conversion")} key="timing">
              <div style={{ padding: 16 }}>
                {timing ? (
                  <>
                    <div className="po2-kpis">
                      <div className="po2-kpi po2-sec">
                        <div className="po2-lab">{t("During-Show Value")}</div>
                        <div className="po2-val">{usd(timing.duringShowValue)}</div>
                      </div>
                      <div className="po2-kpi po2-sec">
                        <div className="po2-lab">{t("After-Show Value")}</div>
                        <div className="po2-val">{usd(timing.afterShowValue)}</div>
                      </div>
                      <div className="po2-kpi po2-sec">
                        <div className="po2-lab">{t("Conversion Rate")}</div>
                        <div className="po2-val">{timing.conversionRatePct != null ? `${timing.conversionRatePct}%` : t("N/A")}</div>
                      </div>
                      <div className="po2-kpi po2-sec">
                        <div className="po2-lab">{t("Avg Days to Commit")}</div>
                        <div className="po2-val">{timing.avgDaysToCommit != null ? timing.avgDaysToCommit.toFixed(1) : t("N/A")}</div>
                      </div>
                    </div>
                    {timingChartData && (
                      <div className="po2-panel">
                        <div className="po2-ph"><h3>{t("Order value by day")}</h3></div>
                        <div className="po2-chartwrap">
                          <Bar data={timingChartData} />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="po2-empty"><p>{t("No data yet.")}</p></div>
                )}
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab={t("Relationships & Mix")} key="relationships">
              <div style={{ padding: 16 }}>
                {relationships ? (
                  <>
                    <div className="po2-panel po2-flush">
                      <div className="po2-ph"><h3>{t("First-Time Trading Pairs")}</h3></div>
                      <div className="po2-tblscroll">
                        <table className="po2-dt">
                          <thead>
                            <tr>
                              <th>{t("Vendor")}</th>
                              <th>{t("Retailer")}</th>
                              <th>{t("Order #")}</th>
                              <th>{t("Date")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(relationships.firstTimePairs || []).map((r) => (
                              <tr key={`${r.vendorDealerId}-${r.retailerDealerId}`}>
                                <td>{r.vendorCompanyName}</td>
                                <td>{r.retailerCompanyName}</td>
                                <td>
                                  <button className="po2-btn po2-linky" onClick={() => navigate(`/orderDetail/${r.poId}`)}>
                                    {r.poId}
                                  </button>
                                </td>
                                <td className="po2-sub">{new Date(r.createdDate).toLocaleDateString()}</td>
                              </tr>
                            ))}
                            {(relationships.firstTimePairs || []).length === 0 && (
                              <tr><td colSpan={4}><div className="po2-empty"><p>{t("No data yet.")}</p></div></td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="po2-panel po2-flush">
                      <div className="po2-ph"><h3>{t("Price Band Mix")}</h3></div>
                      <div className="po2-tblscroll">
                        <table className="po2-dt">
                          <thead>
                            <tr>
                              <th>{t("Price Band")}</th>
                              <th className="po2-r">{t("Lines")}</th>
                              <th className="po2-r">{t("Units")}</th>
                              <th className="po2-r">{t("Total Value")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(relationships.priceBandMix || []).map((r) => (
                              <tr key={r.priceBand}>
                                <td>{r.priceBand}</td>
                                <td className="po2-r">{r.lineCount}</td>
                                <td className="po2-r">{r.unitCount}</td>
                                <td className="po2-r"><b>{usd(r.totalValue)}</b></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {!relationships.categoryMixAvailable && (
                      <div className="po2-insight po2-lock">
                        {t("Category mix is not yet available - product category is only tracked in the catalog search index, not in order data.")}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="po2-empty"><p>{t("No data yet.")}</p></div>
                )}
              </div>
            </Tabs.TabPane>
          </Tabs>
        </div>
      </Spin>
    </div>
  );
};

export default OrganizerDashboard;
